/**
 * Testimonials API client.
 *
 * Public read (no auth) of published testimonials; submission requires a
 * logged-in student (JWT from localStorage). Talks to student-agent-api
 * /testimonials/* (proxied via /api).
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export type MediaType = 'text' | 'audio' | 'video_link';

export interface Testimonial {
  id: string;
  author_name: string;
  author_detail: string | null;
  author_avatar_url: string | null;
  quote: string | null;
  media_type: MediaType;
  audio_url: string | null;
  audio_duration_s: number | null;
  external_url: string | null;
  external_platform: string | null;
  rating: number | null;
  featured: boolean;
  /** Hand-picked by the team (show_in_banner). Surfaces first in the feed. */
  curated?: boolean;
  like_count: number;
  tags: string[];
  published_at: string | null;
}

export interface TestimonialTag {
  tag: string;
  count: number;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const testimonialsAPI = {
  async list(opts?: {
    media_type?: MediaType;
    tag?: string;
    banner?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (opts?.media_type) params.set('media_type', opts.media_type);
    if (opts?.tag) params.set('tag', opts.tag);
    if (opts?.banner) params.set('banner', 'true');
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    const url = `${API_BASE_URL}/testimonials${params.toString() ? `?${params}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to load testimonials (${res.status})`);
    return (await res.json()) as {
      testimonials: Testimonial[];
      total: number;
      limit: number;
      offset: number;
    };
  },

  async like(id: string, undo = false): Promise<number> {
    const res = await fetch(
      `${API_BASE_URL}/testimonials/${id}/like${undo ? '?undo=true' : ''}`,
      { method: 'POST' }
    );
    if (!res.ok) throw new Error(`Like failed (${res.status})`);
    const d = await res.json();
    return d.like_count as number;
  },

  async listTags(): Promise<TestimonialTag[]> {
    const res = await fetch(`${API_BASE_URL}/testimonials/tags`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.tags || [];
  },

  async submit(body: {
    quote?: string;
    external_url?: string;
    author_name?: string;
    author_detail?: string;
    consent: boolean;
    tags?: string[];
  }) {
    const res = await fetch(`${API_BASE_URL}/testimonials/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    if (res.status === 401) throw new Error('AUTH');
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || `Submission failed (${res.status})`);
    }
    return await res.json();
  },

  async submitVoice(blob: Blob, opts: {
    author_name?: string;
    author_detail?: string;
    duration_s?: number;
    consent: boolean;
  }) {
    const form = new FormData();
    const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';
    form.append('audio', blob, `testimonial.${ext}`);
    if (opts.author_name) form.append('author_name', opts.author_name);
    if (opts.author_detail) form.append('author_detail', opts.author_detail);
    if (opts.duration_s != null) form.append('duration_s', String(opts.duration_s));
    form.append('consent', String(opts.consent));
    const res = await fetch(`${API_BASE_URL}/testimonials/submit/voice`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body: form,
    });
    if (res.status === 401) throw new Error('AUTH');
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || `Voice submission failed (${res.status})`);
    }
    return await res.json();
  },
};
